import { action, computed, observable } from 'mobx';

import httpFacade from 'http/httpFacade';

import { DatasetTransferParameters, IDataset } from './types';

import { DEFAULT_PAGE, DEFAULT_PAGE_SIZE } from '../constants';

import Log from 'helpers/log';
import { SortConfig } from 'helpers/types';
import { sortByAccessor } from 'helpers/accessors';
import { PromiseService } from 'services/PromiseService';

class DatasetsStore {
  @observable private _datasets: IDataset[] = [];

  @observable pageCount: number;
  @observable size = DEFAULT_PAGE_SIZE;
  @observable page = DEFAULT_PAGE;
  @observable searchValue = '';

  @observable private _promiseService = new PromiseService();

  private defaultSort: SortConfig = {
    accessor: 'lastModifiedDate',
    desc: true,
  };

  @action.bound
  async fetchDatasets() {
    try {
      const { data } = await this._promiseService.add(
        httpFacade.recognition.fetchDatasets,
      );

      this._datasets = data;
    } catch (error) {
      Log.info(error);
    }
  }

  @action.bound
  async createEmptyDataset() {
    try {
      const formData = new FormData();

      const parameters: DatasetTransferParameters = {
        deleteExistingSamplesBeforeImport: false,
        absentLabellingAllowed: true,
        generateNewSampleName: true,
        chunkSize: 100,
      };
      const parametersJSON = JSON.stringify(parameters);
      const emptyFile = new File([], 'emptyDataset');

      formData.append('parameters', parametersJSON);
      formData.append('file', emptyFile);

      const { data } = await this._promiseService.add(() =>
        httpFacade.recognition.transferDataset(formData),
      );

      this._datasets.push(data.dataset);
    } catch (error) {
      Log.info(error);
    }
  }

  @action.bound
  async deleteDataset(id: string) {
    try {
      const dto = this.datasets.find(dataset => dataset.id === id);

      if (dto) {
        await this._promiseService.add(() =>
          httpFacade.recognition.deletedDataset(id, {
            id: dto!.id,
            version: dto!.version,
            name: dto!.name,
          }),
        );

        this._datasets = this._datasets.filter(dataset => dataset.id !== id);
      }
    } catch (error) {
      Log.info(error);
    }
  }

  @computed
  get datasets() {
    return [...this._datasets].sort(sortByAccessor(this.defaultSort));
  }

  @computed
  get filteredItems() {
    return this.datasets.filter(el =>
      this.searchValue.trim()
        ? el.name
            .toLowerCase()
            .includes(this.searchValue.trim().toLocaleLowerCase())
        : el,
    );
  }

  @computed
  get loading() {
    return this._promiseService.isPending;
  }
}

export default DatasetsStore;
